import React, { Component } from 'react';
import {
  PieChart, Pie, Cell, Label,
} from 'recharts';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import './CashFlow.css';
import { editQuestionnaire, mapExpenseTypeToSection } from 'Consts/Questionnaire';
import EditButtonLink from 'Components/Blueprint/UI/EditButtonLink';
import ExpensesTooltip from './ExpenseTooltip';

const COLOR_MAP = {
  housing: '#14516b',
  taxes: '#f7901e',
  lifestyle: '#3394ba',
  transportation: '#53b4e5',
  savings: '#50b688',
  debt: '#93d8f3',
};

const CustomLabel = ({ viewBox, value1, value2 }) => {
  return (
    <svg {...viewBox}>
      <text
        x="50%"
        y="50%"
        className="recharts-text recharts-label"
        textAnchor="middle"
        dominantBaseline="central"
      >
        <tspan
          className="totalIncomeNumber"
          dy="-0.3em"
          dominantBaseline="central"
          fontSize="26"
        >
          {value1}
        </tspan>
      </text>
      <text
        x="50%"
        y="50%"
        className="recharts-text recharts-label"
        textAnchor="middle"
        dominantBaseline="central"
      >
        <tspan
          className="totalIncomeText"
          dominantBaseline="central"
          dy="1.5em"
          fontSize="14"
        >
          {value2}
        </tspan>
      </text>
    </svg>
  );
};

CustomLabel.propTypes = {
  viewBox: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  value1: PropTypes.string.isRequired,
  value2: PropTypes.string.isRequired,
};

CustomLabel.defaultProps = {
  viewBox: PropTypes.shape({
    cx: null,
    cy: null,
  }),
};


function getEditLink(page, section, householdId, isAgent) {
  return editQuestionnaire(page, {
    section,
    householdId,
    isAgent,
  });
}

const renderCashFlowItem = (data, householdId, isAgent) => data.map((item, index) => {
  const capitalized = `${item.category
    .charAt(0)
    .toUpperCase()}${item.category.slice(1)}`;
  const section = mapExpenseTypeToSection(item.category);

  const editLink = getEditLink('expenses', section, householdId, isAgent);

  return (
    <div className={`item ${item.category}`} key={`${item.category}`}>
      <ExpensesTooltip
        householdId={householdId}
        expenses={item.expenses}
        category={item.category}
      >
        <div className="category">
          <div className="categoryEdit">
            {editLink && (<EditButtonLink
              title={`Edit ${capitalized} Expense`}
              to={editLink}
            />)}
          </div>
          <div className="categoryContents">
            <div className="categoryContentItem">
              {index === data.length - 1 && '*'}
              {`${capitalized}  - ${numeral(item.percent).format('0%')}`}
            </div>
            <div className="categoryContentItem">{numeral(item.value).format('$0,0[.]00')}</div>
          </div>
        </div>
      </ExpensesTooltip>
    </div>
  );
});

class CashFlow extends Component {
  getTotalExpenses() {
    const {
      cashFlow: {
        housingTotal,
        savingsTotal,
        transportationTotal,
        debtTotal,
        lifestyleTotal,
        taxesTotal,
      },
    } = this.props;
    return (
      parseInt(housingTotal, 10)
      + parseInt(savingsTotal, 10)
      + parseInt(transportationTotal, 10)
      + parseInt(debtTotal, 10)
      + parseInt(lifestyleTotal, 10)
      + parseInt(taxesTotal, 10)
    );
  }

  normalizeData() {
    const {
      cashFlow: {
        housingTotal,
        housingPercentage,
        housingExpenses,
        savingsTotal,
        savingsPercentage,
        savingsExpenses,
        transportationTotal,
        transportationPercentage,
        transportationExpenses,
        debtTotal,
        debtPercentage,
        debtExpenses,
        lifestyleTotal,
        lifestylePercentage,
        lifestyleExpenses,
        taxesTotal,
        taxesPercentage,
        taxExpenses,
      },
    } = this.props;

    const data = [
      {
        category: 'housing',
        value: +housingTotal,
        percent: +housingPercentage,
        expenses: housingExpenses,
      },
      {
        category: 'savings',
        value: +savingsTotal,
        percent: +savingsPercentage,
        expenses: savingsExpenses,
      },
      {
        category: 'transportation',
        value: +transportationTotal,
        percent: +transportationPercentage,
        expenses: transportationExpenses,
      },
      {
        category: 'debt',
        value: +debtTotal,
        percent: +debtPercentage,
        expenses: debtExpenses,
      },
      {
        category: 'lifestyle',
        value: +lifestyleTotal,
        percent: +lifestylePercentage,
        expenses: lifestyleExpenses,
      },
      {
        category: 'taxes',
        value: +taxesTotal,
        percent: +taxesPercentage,
        expenses: taxExpenses,
      },
    ];

    return data;
  }

  shapeData() {
    const data = this.normalizeData();
    const showDefault = data.filter(d => d.value !== 0).length === 0;
    const defaultData = [
      {
        name: 'none',
        y: 1,
        color: '#d2d3d4',
      },
    ];

    return showDefault
      ? defaultData
      : data.map(item => ({
        name: item.category,
        y: item.value,
        color: COLOR_MAP[item.category],
      }));
  }

  render() {
    const { grossTotalIncome, householdId, isAgent } = this.props;
    const data = this.normalizeData();

    const remainder = Math.round(grossTotalIncome - this.getTotalExpenses());
    return (
      <div className="cashflow">
        <h3>CashFlow</h3>
        <div className="content">
          <div className="legend">{renderCashFlowItem(data, householdId, isAgent)}</div>
          <div className="cashflow-chart">
            <PieChart width={225} height={225}>
              <Pie
                data={data}
                dataKey="value"
                innerRadius={86}
                outerRadius={100}
                fill="#82ca9d"
              >
                <Label
                  width={30}
                  position="center"
                  content={
                    <CustomLabel
                      value1={numeral(Math.round(grossTotalIncome)).format(
                        '$0,0[.]00',
                      )}
                      value2="Gross Total Income"
                    />
                  }
                />
                {data.map(entry => (
                  <Cell
                    key={`${entry.category}-${entry.value}`}
                    fill={COLOR_MAP[entry.category]}
                    stroke={COLOR_MAP[entry.category]}
                  />
                ))}
              </Pie>
            </PieChart>
            <div className="remainder">
              <div
                className={`total ${remainder >= 0 ? 'positive' : 'negative'}`}
              >
                {numeral(remainder).format('$0,0[.]00')}
              </div>
              <div className="header">Remainder</div>
            </div>
          </div>
          <div>
            <sup>*</sup>
            Debt does not include mortgage payments (Housing) or car payments
            (Transportation)
          </div>
        </div>
      </div>
    );
  }
}

CashFlow.propTypes = {
  cashFlow: PropTypes.object.isRequired,
  grossTotalIncome: PropTypes.number.isRequired,
  householdId: PropTypes.number.isRequired,
  isAgent: PropTypes.bool.isRequired,
};

export default CashFlow;
